import { DOC_STAGES, docStage, type DocStatus } from '@siderproj/core';
import type { DocFlowInfo } from '../api';
import { fmtDateTime } from '../format';

const STAGE_OF_STATUS = (s: string) => docStage(s as DocStatus);
/** Situação que volta o documento para o chefe da disciplina, com o motivo em destaque. */
const RETURNED: Record<string, string> = { 'Comentado': 'Devolvido pelo cliente com comentários', 'Rejeitado': 'Rejeitado pelo cliente' };

/**
 * Imagem do fluxo de aprovação: Elaboração (chefe da disciplina) › Gerente do projeto › Diretor de engenharia › Cliente › Aprovado.
 * Mostra a etapa atual, quem responde por ela e, nas etapas já cumpridas desta revisão, quem passou adiante e quando.
 */
export function DocFlow({ status, flow, revision }: { status: string; flow: DocFlowInfo; revision: string | null }) {
  const cur = flow.stage;
  if (cur === null) return <div className="docflow off" role="img" aria-label={`Documento ${status.toLowerCase()}: fora do fluxo de aprovação`}><span className="small muted">Documento {status.toLowerCase()}: fora do fluxo de aprovação.</span></div>;
  const who = (k: string): string[] => k === 'elaboracao' ? flow.people.chefe : k === 'gerente' ? flow.people.gerente : k === 'diretor' ? flow.people.diretor : k === 'cliente' ? (flow.people.cliente.length ? flow.people.cliente : flow.people.clienteOrg ? [flow.people.clienteOrg] : []) : [];
  /** última passagem desta revisão que saiu de cada etapa para a seguinte */
  const passed = (i: number) => [...flow.history].reverse().find(h => STAGE_OF_STATUS(h.from) === i && (STAGE_OF_STATUS(h.to) ?? -1) > i);
  const back = [...flow.history].reverse().find(h => (STAGE_OF_STATUS(h.to) ?? 9) < (STAGE_OF_STATUS(h.from) ?? 0));
  const returned = RETURNED[status] ?? (cur === 0 && back && back === flow.history[flow.history.length - 1] ? `Devolvido por ${back.user}` : null);
  const label = `Etapa atual: ${cur === 4 ? 'aprovado' : DOC_STAGES[cur]!.label}${returned ? ` (${returned.toLowerCase()})` : ''}`;
  return (
    <div className="docflow" role="img" aria-label={label}>
      <ol>
        {DOC_STAGES.map((s, i) => {
          const state = i < cur || (i === 4 && cur === 4) ? 'done' : i === cur ? (returned ? 'back' : 'cur') : 'todo';
          const p = passed(i); const names = who(s.key);
          return (
            <li key={s.key} className={state}>
              <span className="dot" aria-hidden="true">{state === 'done' ? <svg viewBox="0 0 24 24"><path d="M5 12l5 5 9-10" /></svg> : state === 'back' ? '!' : i + 1}</span>
              <span className="lbl">{i === 0 ? 'Elaboração' : s.label}</span>
              <span className="who">
                {i === 0 ? <>{flow.people.chefeIsCoordinator ? 'Chefe da disciplina' : 'Chefe da disciplina (não cadastrado)'}<br /></> : null}
                {names.length ? names.join(', ') : i === 4 ? '' : i === 0 ? 'o gerente faz esta etapa' : <em>ninguém na equipe</em>}
              </span>
              {state === 'cur' && i < 4 && <span className="when now">Aguardando{revision ? ` · rev. ${revision}` : ''}</span>}
              {state === 'back' && <span className="when warn">{returned}</span>}
              {state === 'done' && p && <span className="when">{p.user} · {fmtDateTime(p.at)}</span>}
            </li>
          );
        })}
      </ol>
      {returned && back?.note && <p className="small" style={{ margin: '6px 0 0' }}><span className="muted">Motivo:</span> {back.note}</p>}
    </div>
  );
}

/** Versão compacta para a lista: cinco pontos, o atual destacado. */
export function DocFlowMini({ status }: { status: string }) {
  const cur = STAGE_OF_STATUS(status);
  if (cur === null) return <span className="small muted">fora do fluxo</span>;
  const back = status === 'Comentado' || status === 'Rejeitado';
  const name = cur === 4 ? 'Aprovado' : DOC_STAGES[cur]!.label;
  return (
    <span className="docflow-mini" role="img" aria-label={`Etapa: ${name}`} title={`Etapa: ${name}${back ? ` (${status.toLowerCase()})` : ''}`}>
      {DOC_STAGES.map((s, i) => <i key={s.key} className={i < cur || cur === 4 ? 'done' : i === cur ? (back ? 'back' : 'cur') : ''} />)}
      <b>{cur === 0 ? 'Chefe' : cur === 1 ? 'Gerente' : cur === 2 ? 'Diretor' : cur === 3 ? 'Cliente' : 'Aprovado'}</b>
    </span>
  );
}
